import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  RefreshCw, Download, Users, IndianRupee, Truck, PackageCheck, PackageX, Clock, Ban,
  Building2, PieChart, UserCheck, ShoppingBag, Search,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { getAgentDashboard, getAgentLeads, type AgentDetailRequest, type AgentStage } from '@/services/reportsService';
import AgentOrdersModal from '@/components/reports/AgentOrdersModal';
import AgentLeadsModal from '@/components/reports/AgentLeadsModal';
import { InfoTip, ReportNotes } from '@/pages/reports/_Components/InfoTip';
import { downloadCSV, type CsvColumn } from '@/utils/csv';
import { toastMessage } from '@/utils/toast-message';

/** "aereka RICHA" -> "Aereka Richa" */
const titleCase = (n: string) =>
  (n || '').toLowerCase().replace(/(^|[\s.'-])([a-z])/g, (_, a, b) => a + b.toUpperCase());

/** One money figure split the way every card and row is split. Stages add up to booked. */
interface Money {
  orders: number;
  booked: number;
  units: number;
  aov: number;
  delivery_pct: number | null;
  rto_pct: number | null;
  delivered_orders: number; delivered_value: number;
  in_transit_orders: number; in_transit_value: number;
  awaiting_pickup_orders: number; awaiting_pickup_value: number;
  returned_orders: number; returned_value: number;
  cancelled_orders: number; cancelled_value: number;
  cod_orders: number; cod_value: number;
  prepaid_orders: number; prepaid_value: number;
  partial_orders: number; partial_value: number;
}
interface AgentRow extends Money {
  user_id: number;
  user_uuid: string;
  agent_name: string;
  is_active: boolean;
  leads: number;
  conversion_pct: number | null;
}
interface Summary {
  agents: Money & { leads: number; conversion_pct: number | null };
  agent_count: number;
  revenue_per_agent: number;
  company?: Money;
  agent_share_pct?: number | null;
}
type Reason = 'tag_unmatched' | 'web_agent_lead' | 'web_unassigned' | 'web_no_lead';
interface UnattributedRow extends Money { reason: Reason; }
interface DashboardResponse {
  success: boolean; data: AgentRow[]; unattributed?: UnattributedRow[]; summary: Summary; own_only: boolean;
  data_as_of?: string | null;
}

// Same four reasons, and the same wording, as the Agent Leaderboard.
const REASON_LABEL: Record<Reason, { label: string; hint: string }> = {
  tag_unmatched:  { label: 'Tag not recognised', hint: 'The order carries a Sales tag that matches no agent — fix the tag spelling or add it as an alias.' },
  web_agent_lead: { label: 'Website · agent owns the lead', hint: 'Customer ordered on the website with no agent tag or code, but an agent owns the customer.' },
  web_unassigned: { label: 'Website · lead unassigned', hint: 'Customer ordered on the website; the customer is in the CRM but no agent owns them.' },
  web_no_lead:    { label: 'Website · no CRM lead', hint: 'Order not linked to any CRM customer.' },
};

type Bucket = '' | 'abandoned_cart' | 'reorder' | 'rto' | 'none';
type SortKey = 'agent_name' | 'leads' | 'orders' | 'booked' | 'delivered_value' | 'in_transit_value'
  | 'awaiting_pickup_value' | 'returned_value' | 'cancelled_value' | 'delivery_pct' | 'rto_pct' | 'aov' | 'conversion_pct';

const iso = (d: Date) => d.toISOString().slice(0, 10);
const today = iso(new Date());
const monthStart = iso(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
const inr = (v: number | null | undefined) => `₹${Math.round(v || 0).toLocaleString('en-IN')}`;
const lakh = (v: number | null | undefined) => {
  const n = v || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return inr(n);
};
const num = (v: number | null | undefined) => (v || 0).toLocaleString('en-IN');
/** "₹2,17,845 (30)" -- value with its order count. */
const inrN = (v: number, n: number) => `${inr(v)} (${num(n)})`;
const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v}%`);

const STAGES: { key: AgentStage; label: string; color: string; icon: React.ReactNode; info: string }[] = [
  { key: 'delivered', label: 'Delivered', color: '#10b981', icon: <PackageCheck className="h-4 w-4" />,
    info: 'Money realised: Clickpost Delivered (8) or Partially Delivered (48).' },
  { key: 'in_transit', label: 'In Transit', color: '#0ea5e9', icon: <Truck className="h-4 w-4" />,
    info: 'Picked up and on the way, including a failed attempt that will be retried.' },
  { key: 'awaiting_pickup', label: 'Awaiting Pickup', color: '#a3a3a3', icon: <Clock className="h-4 w-4" />,
    info: 'No courier scan yet, or only a label (AWB Registered / Pickup Pending / Pickup Failed).' },
  { key: 'returned', label: 'Returned (RTO)', color: '#ef4444', icon: <PackageX className="h-4 w-4" />,
    info: 'Returning or returned to us (any RTO status), or lost / damaged in transit.' },
  { key: 'cancelled', label: 'Cancelled', color: '#6b7280', icon: <Ban className="h-4 w-4" />,
    info: 'Cancelled, voided or refunded before delivery, or cancelled by the courier.' },
];

const Card: React.FC<{
  label: string; value: string; sub?: string; info?: string; icon?: React.ReactNode;
  accent?: string; onClick?: () => void;
}> = ({ label, value, sub, info, icon, accent = 'border-brandColor', onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={`text-left bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 ${accent} px-4 py-3 ${onClick ? 'hover:shadow-md cursor-pointer' : 'cursor-default'} transition-shadow`}
  >
    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
      {icon}{label}{info && <InfoTip text={info} />}
    </p>
    <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">{value}</p>
    {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
  </button>
);

const AgentDashboard = () => {
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [bucket, setBucket] = useState<Bucket>('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('booked');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [ordersModal, setOrdersModal] = useState<{ open: boolean; title: string; req: AgentDetailRequest }>(
    { open: false, title: '', req: { from_date: '', to_date: '' } });
  const [leadsModal, setLeadsModal] = useState<{ open: boolean; title: string; req: AgentDetailRequest }>(
    { open: false, title: '', req: { from_date: '', to_date: '' } });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAgentDashboard({ from_date: fromDate, to_date: toDate, lead_bucket: bucket || null }) as unknown as DashboardResponse;
      if (res?.success) setData(res);
      else toastMessage('Failed to load the Agent Dashboard', 'error');
    } catch {
      toastMessage('Error loading the Agent Dashboard', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, bucket]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const baseReq = (): AgentDetailRequest => ({ from_date: fromDate, to_date: toDate, lead_bucket: bucket || null });
  const openOrders = (title: string, extra: Partial<AgentDetailRequest>) =>
    setOrdersModal({ open: true, title, req: { ...baseReq(), ...extra } });
  const openLeads = (title: string, agent_uuid?: string) =>
    setLeadsModal({ open: true, title, req: { ...baseReq(), agent_uuid: agent_uuid || null } });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.data || []).filter(r => !q || r.agent_name.toLowerCase().includes(q));
    return [...list].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string' || typeof bv === 'string')
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      const an = (av ?? -1) as number, bn = (bv ?? -1) as number;
      return sortDir === 'asc' ? an - bn : bn - an;
    });
  }, [data, search, sortKey, sortDir]);

  const chartData = useMemo(() => [...(data?.data || [])]
    .filter(r => r.orders > 0)
    .sort((a, b) => b.booked - a.booked)
    .slice(0, 15)
    .map(r => ({
      name: titleCase(r.agent_name).length > 16 ? `${titleCase(r.agent_name).slice(0, 15)}…` : titleCase(r.agent_name),
      Delivered: r.delivered_value, 'In Transit': r.in_transit_value, 'Awaiting Pickup': r.awaiting_pickup_value,
      'Returned (RTO)': r.returned_value, Cancelled: r.cancelled_value,
    })), [data]);

  const sortBy = (k: SortKey) => {
    if (k === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'agent_name' ? 'asc' : 'desc'); }
  };

  const exportTable = () => {
    const cols: CsvColumn<AgentRow>[] = [
      { header: 'Agent', accessor: r => titleCase(r.agent_name) },
      { header: 'Active', accessor: r => (r.is_active ? 'Yes' : 'No') },
      { header: 'Orders', accessor: 'orders' },
      { header: 'Conv%', accessor: r => r.conversion_pct ?? '' },
      { header: 'Booked', accessor: 'booked' },
      { header: 'Delivered', accessor: 'delivered_value' },
      { header: 'In Transit', accessor: 'in_transit_value' },
      { header: 'Awaiting Pickup', accessor: 'awaiting_pickup_value' },
      { header: 'Returned (RTO)', accessor: 'returned_value' },
      { header: 'Cancelled', accessor: 'cancelled_value' },
      { header: 'Delivery %', accessor: r => r.delivery_pct ?? '' },
      { header: 'RTO %', accessor: r => r.rto_pct ?? '' },
      { header: 'AOV', accessor: 'aov' },
      { header: 'Units', accessor: 'units' },
      { header: 'COD orders', accessor: 'cod_orders' },
      { header: 'Prepaid orders', accessor: 'prepaid_orders' },
      { header: 'Partial orders', accessor: 'partial_orders' },
    ];
    downloadCSV(`agent_dashboard_${fromDate}_to_${toDate}`, cols, rows);
  };

  const exportAllLeads = async () => {
    try {
      const res = await getAgentLeads(baseReq()) as { success?: boolean; data?: Record<string, unknown>[] };
      if (!res?.success || !res.data?.length) { toastMessage('No leads to export', 'error'); return; }
      const keys = Object.keys(res.data[0]);
      downloadCSV(`agent_leads_${fromDate}_to_${toDate}`, keys.map(k => ({ header: k, accessor: (r: Record<string, unknown>) => String(r[k] ?? '') })), res.data);
    } catch {
      toastMessage('Error exporting leads', 'error');
    }
  };

  const s = data?.summary;
  const a = s?.agents;
  const company = s?.company;
  const ownOnly = data?.own_only;
  const unattributed = company && a ? company.booked - a.booked : 0;

  const Th: React.FC<{ k: SortKey; children: React.ReactNode; right?: boolean }> = ({ k, children, right }) => (
    <th onClick={() => sortBy(k)}
        className={`px-3 py-2.5 text-xs font-semibold text-white uppercase tracking-wider whitespace-nowrap cursor-pointer select-none ${right ? 'text-right' : 'text-left'}`}>
      {children}{sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
  const Cell: React.FC<{ v: string; onClick?: () => void; tone?: string }> = ({ v, onClick, tone }) => (
    <td className={`px-3 py-2 text-right whitespace-nowrap ${tone || 'text-gray-700 dark:text-gray-300'}`}>
      {onClick ? <button onClick={onClick} className="hover:underline">{v}</button> : v}
    </td>
  );

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-brandColor p-2 rounded-lg"><Users className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Agent Dashboard <span className="ml-1 align-middle text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Beta</span></h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Revenue earned by each agent — credited when the order is placed, by agent tag or discount code
                {data?.data_as_of && (
                  <span className="ml-2 text-gray-400" title="Summaries refresh every 30 minutes; the order lists you open are live">
                    · Data as of {new Date(data.data_as_of).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!ownOnly && (
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search agent…"
                       className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg pl-7 pr-3 py-1.5 bg-white dark:bg-gray-700 w-40" />
              </div>
            )}
            <select value={bucket} onChange={e => setBucket(e.target.value as Bucket)}
                    title="The customer's list (bucket) the order belongs to"
                    className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700">
              <option value="">All buckets</option>
              <option value="abandoned_cart">Abandoned Cart</option>
              <option value="reorder">Reorder</option>
              <option value="rto">RTO</option>
              <option value="none">No bucket</option>
            </select>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                   className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                   className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-700" />
            <button onClick={fetchData} disabled={loading}
                    className="flex items-center gap-1.5 px-4 py-1.5 bg-brandColor text-white rounded-lg text-sm font-medium disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />{loading ? 'Loading…' : 'Run'}
            </button>
            {data && (
              <>
                <button onClick={exportTable}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm">
                  <Download className="h-4 w-4" />Table
                </button>
                <button onClick={exportAllLeads}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm">
                  <Download className="h-4 w-4" />Leads
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-5">
        {!data && (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
            {loading ? 'Loading…' : 'Choose dates and press Run'}
          </div>
        )}

        {a && (
          <>
            {/* Row 1 -- the headline money, and where it stands. Stages add up to Booked. */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <Card label={ownOnly ? 'My Booked Revenue' : 'Agent Booked Revenue'} value={lakh(a.booked)}
                    sub={`${num(a.orders)} orders · AOV ${inr(a.aov)}`} icon={<IndianRupee className="h-3.5 w-3.5" />}
                    accent="border-indigo-500"
                    info="Total value of every order credited to an agent in these dates, whatever happened to it afterwards. The five cards to the right split this number."
                    onClick={() => openOrders(ownOnly ? 'My orders' : 'All agent orders', { agent_uuid: 'agents' })} />
              {STAGES.map(st => (
                <Card key={st.key} label={st.label}
                      value={lakh(a[`${st.key}_value` as keyof Money] as number)}
                      sub={`${num(a[`${st.key}_orders` as keyof Money] as number)} orders`
                        + (st.key === 'delivered' ? ` · ${pct(a.delivery_pct)} of settled` : '')
                        + (st.key === 'returned' ? ` · ${pct(a.rto_pct)} of settled` : '')}
                      icon={st.icon} info={st.info}
                      accent={{ delivered: 'border-emerald-500', in_transit: 'border-sky-500', awaiting_pickup: 'border-gray-300',
                                returned: 'border-red-500', cancelled: 'border-gray-500' }[st.key]}
                      onClick={() => openOrders(`${st.label} — agent orders`, { agent_uuid: 'agents', delivery_status: st.key })} />
              ))}
            </div>

            {/* Row 2 -- company context (admins) and agent productivity */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              {!ownOnly && company && (
                <>
                  <Card label="Company Revenue" value={lakh(company.booked)} sub={`${num(company.orders)} orders, all channels`}
                        icon={<Building2 className="h-3.5 w-3.5" />} accent="border-slate-500"
                        info="Every order placed in these dates — agent-credited plus website / direct orders nobody earned." />
                  <Card label="Agents' Share" value={pct(s?.agent_share_pct)}
                        sub={`${lakh(unattributed)} not credited to any agent`}
                        icon={<PieChart className="h-3.5 w-3.5" />} accent="border-violet-500"
                        info="Agent booked revenue as a share of company revenue. Click to see the orders no agent earned."
                        onClick={() => openOrders('Orders no agent earned', { agent_uuid: 'none' })} />
                  <Card label="Revenue per Agent" value={lakh(s?.revenue_per_agent)} sub={`${s?.agent_count} agents with orders`}
                        icon={<UserCheck className="h-3.5 w-3.5" />} accent="border-amber-500"
                        info="Agent booked revenue ÷ agents who have at least one order in these dates." />
                </>
              )}
              <Card label="Delivered Revenue per Order" value={inr(a.delivered_orders ? a.delivered_value / a.delivered_orders : 0)}
                    sub={`${num(a.units)} units booked`} icon={<ShoppingBag className="h-3.5 w-3.5" />} accent="border-teal-500"
                    info="Average value of a delivered agent order." />
              <Card label="COD vs Prepaid" value={`${a.orders ? Math.round(100 * a.cod_orders / a.orders) : 0}% COD`}
                    sub={`COD ${lakh(a.cod_value)} · Prepaid ${lakh(a.prepaid_value)} · Partial ${lakh(a.partial_value)}`}
                    accent="border-orange-500"
                    info="Share of agent orders placed as cash on delivery, and the value of each payment type."
                    onClick={() => openOrders('COD agent orders', { agent_uuid: 'agents', payment_status: 'cod' })} />
              <Card label="Leads → Orders" value={pct(a.conversion_pct)} sub={`${num(a.leads)} leads · ${num(a.orders)} orders`}
                    icon={<Users className="h-3.5 w-3.5" />} accent="border-rose-500"
                    info="Credited orders ÷ leads the agents owned or worked in these dates (lead counts refresh nightly)."
                    onClick={() => openLeads(ownOnly ? 'My leads' : 'All agent leads')} />
            </div>

            {!ownOnly && chartData.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Revenue by agent — top 15, and where it stands</p>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => lakh(v as number)} tick={{ fontSize: 11 }} width={70} />
                    <Tooltip formatter={(v) => inr(v as number)} />
                    <Legend verticalAlign="top" height={30} />
                    {STAGES.map(st => (
                      <Bar key={st.key} dataKey={st.label} stackId="rev" fill={st.color} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-brandColor sticky top-0">
                  <tr>
                    <Th k="agent_name">Agent</Th>
                    <Th k="orders" right>Orders</Th>
                    <Th k="conversion_pct" right>Conv%</Th>
                    <Th k="booked" right>Booked</Th>
                    <Th k="delivered_value" right>Delivered</Th>
                    <Th k="in_transit_value" right>In Transit</Th>
                    <Th k="awaiting_pickup_value" right>Awaiting Pickup</Th>
                    <Th k="returned_value" right>Returned</Th>
                    <Th k="cancelled_value" right>Cancelled</Th>
                    <Th k="delivery_pct" right>Delivery %</Th>
                    <Th k="rto_pct" right>RTO %</Th>
                    <Th k="aov" right>AOV</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map(r => {
                    const ord = (title: string, extra: Partial<AgentDetailRequest>) =>
                      openOrders(`${titleCase(r.agent_name)} — ${title}`, { agent_uuid: r.user_uuid, ...extra });
                    return (
                      <tr key={r.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                          {titleCase(r.agent_name)}
                          {!r.is_active && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">left</span>}
                        </td>
                        <Cell v={num(r.orders)} onClick={r.orders ? () => ord('orders', {}) : undefined} />
                        <Cell v={pct(r.conversion_pct)} />
                        <Cell v={inrN(r.booked, r.orders)} tone="font-semibold text-gray-900 dark:text-white" onClick={r.orders ? () => ord('orders', {}) : undefined} />
                        <Cell v={inrN(r.delivered_value, r.delivered_orders)} tone="text-emerald-700" onClick={r.delivered_orders ? () => ord('delivered', { delivery_status: 'delivered' }) : undefined} />
                        <Cell v={inrN(r.in_transit_value, r.in_transit_orders)} onClick={r.in_transit_orders ? () => ord('in transit', { delivery_status: 'in_transit' }) : undefined} />
                        <Cell v={inrN(r.awaiting_pickup_value, r.awaiting_pickup_orders)} onClick={r.awaiting_pickup_orders ? () => ord('awaiting pickup', { delivery_status: 'awaiting_pickup' }) : undefined} />
                        <Cell v={inrN(r.returned_value, r.returned_orders)} tone="text-red-600" onClick={r.returned_orders ? () => ord('returned', { delivery_status: 'returned' }) : undefined} />
                        <Cell v={inrN(r.cancelled_value, r.cancelled_orders)} onClick={r.cancelled_orders ? () => ord('cancelled', { delivery_status: 'cancelled' }) : undefined} />
                        <Cell v={pct(r.delivery_pct)} tone={r.delivery_pct !== null && r.delivery_pct < 50 ? 'text-red-600' : undefined} />
                        <Cell v={pct(r.rto_pct)} tone={r.rto_pct !== null && r.rto_pct > 15 ? 'text-red-600' : undefined} />
                        <Cell v={inr(r.aov)} />
                      </tr>
                    );
                  })}
                  {!rows.length && (
                    <tr><td colSpan={12} className="px-3 py-8 text-center text-gray-400">No agent orders in these dates</td></tr>
                  )}
                  {!ownOnly && (data?.unattributed || []).length > 0 && (
                    <tr className="bg-gray-50 dark:bg-gray-900">
                      <td colSpan={12} className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Not credited to an agent
                      </td>
                    </tr>
                  )}
                  {!ownOnly && (data?.unattributed || []).map(u => {
                    const meta = REASON_LABEL[u.reason];
                    const ord = (title: string, extra: Partial<AgentDetailRequest>) =>
                      openOrders(`${meta?.label || u.reason} — ${title}`, { agent_uuid: `none:${u.reason}`, ...extra });
                    return (
                      <tr key={u.reason} className="bg-gray-50/60 dark:bg-gray-900/40 text-gray-500">
                        <td className="px-3 py-2 whitespace-nowrap" title={meta?.hint}>
                          <span className="font-medium text-gray-700 dark:text-gray-300">{meta?.label || u.reason}</span>
                          <span className="block text-[11px] text-gray-400">not credited to an agent</span>
                        </td>
                        <Cell v={num(u.orders)} onClick={u.orders ? () => ord('orders', {}) : undefined} />
                        <Cell v="—" />
                        <Cell v={inrN(u.booked, u.orders)} tone="font-semibold text-gray-700 dark:text-gray-300" onClick={u.orders ? () => ord('orders', {}) : undefined} />
                        <Cell v={inrN(u.delivered_value, u.delivered_orders)} onClick={u.delivered_orders ? () => ord('delivered', { delivery_status: 'delivered' }) : undefined} />
                        <Cell v={inrN(u.in_transit_value, u.in_transit_orders)} onClick={u.in_transit_orders ? () => ord('in transit', { delivery_status: 'in_transit' }) : undefined} />
                        <Cell v={inrN(u.awaiting_pickup_value, u.awaiting_pickup_orders)} onClick={u.awaiting_pickup_orders ? () => ord('awaiting pickup', { delivery_status: 'awaiting_pickup' }) : undefined} />
                        <Cell v={inrN(u.returned_value, u.returned_orders)} onClick={u.returned_orders ? () => ord('returned', { delivery_status: 'returned' }) : undefined} />
                        <Cell v={inrN(u.cancelled_value, u.cancelled_orders)} onClick={u.cancelled_orders ? () => ord('cancelled', { delivery_status: 'cancelled' }) : undefined} />
                        <Cell v={pct(u.delivery_pct)} />
                        <Cell v={pct(u.rto_pct)} />
                        <Cell v={inr(u.aov)} />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ReportNotes items={[
              { term: 'Who gets an order', def: 'Decided when the order is placed: the agent whose Sales tag is on the Shopify order, otherwise the agent whose discount code was used. It never moves afterwards, even if the customer is later handed to another agent.' },
              { term: 'Booked', def: 'Everything credited in these dates. Delivered + In Transit + Awaiting Pickup + Returned + Cancelled always add up to Booked.' },
              { term: 'Delivery % / RTO %', def: 'Out of orders that have finished their journey (delivered or returned), so recent orders still on the road do not drag the rate down.' },
              { term: 'Dates', def: 'By order date (IST).' },
              { term: 'Freshness', def: 'The cards and table refresh every 30 minutes (see "Data as of" at the top). The order lists you open are live, so they can include a few orders placed since.' },
              { term: 'Agents who left', def: 'Shown with a "left" tag when they have orders in these dates — they still sold what they sold.' },
            ]} />
          </>
        )}
      </div>

      <AgentOrdersModal isOpen={ordersModal.open} onClose={() => setOrdersModal(m => ({ ...m, open: false }))}
                        title={ordersModal.title} request={ordersModal.req} />
      <AgentLeadsModal isOpen={leadsModal.open} onClose={() => setLeadsModal(m => ({ ...m, open: false }))}
                       title={leadsModal.title} request={leadsModal.req} />
    </div>
  );
};

export default AgentDashboard;
